import React, { useState } from 'react';
import classnames from 'classnames';

import { ButtonBase, IconButton, ListItemIcon, Menu, MenuList, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, MenuItem, Translate } from '@hydra/common/components';
import { useContactSupport, useI18n } from '@hydra/common/customHooks';

import { sendEvent } from '@hydra/utils/gtm';

const useStyles = makeStyles(theme => ({
  header: {
    pointerEvents: 'none',
    opacity: '1 !important', // Avoid opacity when element is disabled
    '& > span': {
      fontWeight: theme.typography.fontWeightSemibold,
      fontSize: theme.typography.fontSizeLarge
    },
    '&:focus, &:hover, &:active': {
      textDecoration: 'none'
    }
  },
  helpMenuItem: {
    minWidth: 255,
    maxWidth: 255,
    width: 255,
    '&:hover': {
      '& $helpListItemIcon': {
        '& > svg': {
          fill: theme.palette.primary.main
        }
      },
      '& $externalListIconItem': {
        '& > svg': {
          display: 'inline-block',
          fill: theme.palette.primary.main
        }
      }
    }
  },
  helpListItemIcon: {},
  externalListIconItem: {
    marginLeft: 'auto',
    minWidth: theme.spacing(3),
    '& > svg': {
      display: 'none',
      width: theme.spacing(2),
      height: theme.spacing(2)
    }
  }
}));

interface IRenderTriggerProps {
  'aria-expanded': boolean | undefined
  disableElevation: boolean
  onClick: React.MouseEventHandler
  disabled: boolean
  endIcon: React.ReactNode
}

interface IHelpDropdownProps {
  renderTrigger: (props: IRenderTriggerProps) => React.ReactNode
  contentColor: 'white' | 'gray'
}

export default function HelpDropdown ({ renderTrigger, contentColor }: IHelpDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const classes = useStyles();
  const { locale } = useI18n();

  const { options, isHelpLoading, onClick } = useContactSupport(locale);

  function handleClick (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose () {
    setAnchorEl(null);
  }

  function handleGtmClick (label = '') {
    if (label) {
      sendEvent(label);
    }
  }
  const triggerComponentProps = {
    'aria-expanded': anchorEl ? true : undefined,
    disableElevation: true,
    onClick: handleClick,
    disabled: isHelpLoading,
    endIcon: anchorEl ? <Icon type='chevron-up' color={contentColor} /> : <Icon type='chevron-down' color={contentColor} />
  };

  return (
    <>
      {
        isHelpLoading
          ? (
            <IconButton disabled={isHelpLoading}>
              <Icon type='loader' color={contentColor} />
            </IconButton>
            )
          : renderTrigger(triggerComponentProps)
      }
      <Menu
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuList disablePadding>
          <MenuItem
            className={classes.helpMenuItem}
            href='#'
            onClick={() => {
              onClick();
              handleClose();
            }}
            disabled={isHelpLoading}
            component={ButtonBase}
          >
            <ListItemIcon className={classes.helpListItemIcon}>
              <Icon type='life-buoy' size='md' />
            </ListItemIcon>
            <Typography noWrap component='span'>
              <Translate content='help_dropdown_item_live_assistance' />
            </Typography>
          </MenuItem>
          {
            options.map((item: any, index: number) => (
              <MenuItem
                key={index}
                href={item.link}
                divider={item.divider}
                component={ButtonBase}
                target='_blank'
                disabled={item.header}
                onClick={() => { handleGtmClick(item.gtmLabel); }}
                className={classnames(
                  classes.helpMenuItem,
                  {
                    [classes.header]: item.header
                  }
                )}
              >
                {
                  item.icon && (
                    <ListItemIcon className={classes.helpListItemIcon}>
                      <Icon type={item.icon} size='md' />
                    </ListItemIcon>
                  )
                }
                <Typography noWrap component='span'>
                  <Translate content={item.label} />
                </Typography>
                <ListItemIcon className={classes.externalListIconItem}>
                  <Icon type='external-link' />
                </ListItemIcon>
              </MenuItem>
            ))
          }
        </MenuList>
      </Menu>
    </>
  );
}
